import type { NavigationPane } from 'src/components/Navbar/DesktopDrawer/nav-group-tabs';
import { create, StateCreator } from 'zustand';
import { persist } from 'zustand/middleware';

export type SidekickSubtab = 'policies' | 'ai-chat' | 'help';

export interface SidekickRequest {
  subtab: SidekickSubtab;
  policyId?: string;
}

export interface NavigationStoreState {
  // state variables
  sectionsOpenMap: Record<string, boolean>;
  shouldShowHiddenFolders: boolean;
  // which left-nav grouping pane is active; the shortcut opens the search pane
  // through this, and the navbar keeps it in sync with the current route
  activePane: NavigationPane;
  // one-shot pane request that survives the next route change — redirects that
  // navigate AND want a pane open (e.g. /inbox) set this so the route→pane
  // reconciliation consumes it instead of overwriting with the route's own pane
  pendingPane: NavigationPane | null;
  // one-shot deep link; consumed and cleared by the pane
  sidekickRequest: SidekickRequest | null;
  // until Sidekick has been opened from its new home once, the nav offers the old
  // bottom entry point instead of the grouping tab. Persisted — announced once.
  sidekickTransitioned: boolean;
  // transient cue shown right after that first open
  sidekickMovedCue: boolean;

  // setters
  setSectionOpen: (section: string, isOpen: boolean) => void;
  setShowHiddenFolders: (isOpen: boolean) => void;
  setActivePane: (pane: NavigationPane) => void;
  setPendingPane: (pane: NavigationPane | null) => void;
  requestSidekick: (request: SidekickRequest | null) => void;
  setSidekickTransitioned: (transitioned: boolean) => void;
  setSidekickMovedCue: (visible: boolean) => void;
}

export const createNavigationSlice: StateCreator<
  NavigationStoreState,
  [],
  [],
  NavigationStoreState
> = (set) => ({
  shouldShowHiddenFolders: false,
  sectionsOpenMap: {},
  activePane: 'home',
  pendingPane: null,
  sidekickRequest: null,
  sidekickTransitioned: false,
  sidekickMovedCue: false,

  setSectionOpen: (section: string, isOpen: boolean) =>
    set((state) => ({
      sectionsOpenMap: {
        ...state.sectionsOpenMap,
        [section]: isOpen,
      },
    })),

  setShowHiddenFolders: (shouldShowHiddenFolders: boolean) =>
    set(() => ({
      shouldShowHiddenFolders,
    })),

  setActivePane: (activePane: NavigationPane) => set(() => ({ activePane })),

  setPendingPane: (pendingPane: NavigationPane | null) => set(() => ({ pendingPane })),

  requestSidekick: (sidekickRequest: SidekickRequest | null) =>
    set(() => ({ sidekickRequest })),

  setSidekickTransitioned: (sidekickTransitioned: boolean) =>
    set(() => ({ sidekickTransitioned })),

  setSidekickMovedCue: (sidekickMovedCue: boolean) => set(() => ({ sidekickMovedCue })),
});

const useNavigationStore = create<NavigationStoreState>()(
  persist(
    (...args) => ({
      ...createNavigationSlice(...args),
    }),
    {
      name: 'portal-navigation-store',
      // activePane is derived from the route at runtime — don't persist it
      partialize: ({ sectionsOpenMap, shouldShowHiddenFolders, sidekickTransitioned }) => ({
        sectionsOpenMap,
        shouldShowHiddenFolders,
        sidekickTransitioned,
      }),
    },
  ),
);

export default useNavigationStore;
